import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Building2, Mail, Phone, CheckCircle, XCircle, Info } from 'lucide-react';
import { ShipperTransportJob } from '@/hooks/useShipperJobs';
import { TransporterDetailsDialog } from './TransporterDetailsDialog';
import { useTranslation } from 'react-i18next';

interface PendingReservationCardProps {
  job: ShipperTransportJob;
  onConfirm: () => void;
  onDecline: () => void;
  isConfirming?: boolean;
  isDeclining?: boolean;
}

export const PendingReservationCard = ({
  job,
  onConfirm,
  onDecline,
  isConfirming,
  isDeclining,
}: PendingReservationCardProps) => {
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = useState(false);
  const order = job.orders;
  const company = job.companies;

  return (
    <Card className="rounded-xl shadow-md hover:shadow-lg transition-all duration-300 border-2 border-[#F2AC57]">
      <CardContent className="p-6 space-y-4">
        {/* Header - Minimal Order Info */}
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-1">
              <Badge className="bg-[#F2AC57] text-white">{t('shipper.reservation.pending')}</Badge>
            </div>
            <p className="text-sm text-[#5A6B7A]">
              {t('orders.card.order')} {order?.order_number} - {order?.cargo_title || order?.cargo_description}
            </p>
          </div>
        </div>

        {/* Transporter Info - Enhanced */}
        <div className="space-y-4 border-t border-[#DBE2EA] pt-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3 flex-1">
              <div className="w-12 h-12 rounded-lg bg-[#0880AE]/10 flex items-center justify-center flex-shrink-0">
                <Building2 size={24} className="text-[#0880AE]" />
              </div>
              <div className="flex-1">
                <p className="text-lg font-semibold text-[#2D3748] mb-1">{company?.name}</p>
                <p className="text-xs text-[#5A6B7A] uppercase tracking-wide mb-2">{t('shipper.reservation.transporter')}</p>
                
                <div className="space-y-2">
                  {company?.email && (
                    <div className="flex items-center gap-2">
                      <Mail size={14} className="text-[#0880AE]" />
                      <a 
                        href={`mailto:${company.email}`}
                        className="text-sm text-[#2D3748] hover:text-[#0880AE] transition-colors"
                      >
                        {company.email}
                      </a>
                    </div>
                  )}
                  {company?.phone && (
                    <div className="flex items-center gap-2">
                      <Phone size={14} className="text-[#0880AE]" />
                      <a 
                        href={`tel:${company.phone}`}
                        className="text-sm text-[#2D3748] hover:text-[#0880AE] transition-colors"
                      >
                        {company.phone}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowDetails(true)}
              className="border-[#0880AE] text-[#0880AE] hover:bg-[#0880AE]/10 rounded-lg ml-3 w-9 h-9 p-0"
            >
              <Info size={16} />
            </Button>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 border-t border-[#DBE2EA] pt-4">
          <Button
            onClick={onConfirm}
            disabled={isConfirming || isDeclining}
            className="flex-1 bg-[#14A38B] hover:bg-[#10836F] text-white font-semibold rounded-lg gap-2"
          >
            <CheckCircle size={18} />
            {isConfirming ? t('shipper.reservation.confirming') : t('shipper.reservation.confirm')}
          </Button>
          <Button
            onClick={onDecline}
            disabled={isConfirming || isDeclining}
            variant="outline"
            className="flex-1 border-[#FF7171] text-[#FF7171] hover:bg-[#FF7171]/10 font-semibold rounded-lg gap-2"
          >
            <XCircle size={18} />
            {isDeclining ? t('shipper.reservation.declining') : t('shipper.reservation.decline')}
          </Button>
        </div>
      </CardContent>

      <TransporterDetailsDialog
        open={showDetails}
        onOpenChange={setShowDetails}
        company={company}
        orderNumber={order?.order_number}
      />
    </Card>
  );
};
